import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { consultationStatuses, LeadPage, LeadUpdate, leadError, objections, offerStatuses, PitruLead, pitruLeadService, qualificationStatuses } from '../../services/pitruLeadService';
import '../PitruConsultation/PitruConsultation.css';

const label = (value: string) => value.toLowerCase().replace(/_/g, ' ');
export default function PitruLeadAdmin() {
  const { isAdmin } = useAuth();
  return isAdmin() ? <LeadQueue /> : <p role="alert">Administrator access is required.</p>;
}

export function LeadQueue() {
  const [page, setPage] = useState(0);
  const [data, setData] = useState<LeadPage>();
  const [selected, setSelected] = useState<PitruLead>();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setBusy(true); setError('');
    pitruLeadService.list(page).then(result => { if (active) setData(result); })
      .catch(e => { if (active) setError(leadError(e)); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [page, refresh]);
  const open = async (id: string) => {
    setBusy(true); setError(''); setMessage('');
    try { setSelected(await pitruLeadService.get(id)); }
    catch (e) { setError(leadError(e)); }
    finally { setBusy(false); }
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || busy) return;
    setBusy(true); setError(''); setMessage('');
    const update: LeadUpdate = { version: selected.version, qualificationStatus: selected.qualificationStatus,
      consultationStatus: selected.consultationStatus, offerStatus: selected.offerStatus,
      primaryObjection: selected.primaryObjection, operatorNotes: selected.operatorNotes };
    try {
      const saved = await pitruLeadService.update(selected.id, update);
      setSelected(saved); setData(old => old && ({ ...old, content: old.content.map(lead => lead.id === saved.id ? saved : lead) }));
      setMessage('Lead updated.');
    } catch (e) { setError(leadError(e)); }
    finally { setBusy(false); }
  };
  const select = (key: 'qualificationStatus' | 'consultationStatus' | 'offerStatus' | 'primaryObjection', values: readonly string[], title: string) => <label>{title}
    <select value={selected?.[key] || ''} onChange={e => setSelected(old => old && ({ ...old, [key]: e.target.value || null }))}>
      {key === 'primaryObjection' && <option value="">Not recorded</option>}
      {values.map(value => <option key={value} value={value}>{label(value)}</option>)}
    </select>
  </label>;
  return <section className="pp-v2-admin">
    <h2>Pitru Paksha V2 consultation leads</h2>
    <p>Review answers before classifying. Record real conversations and the main objection; no payment is taken from this view.</p>
    {error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}
    {selected ? <>
      <div className="pp-v2-actions"><button disabled={busy} onClick={() => { setSelected(undefined); setMessage(''); }}>Back to leads</button><button disabled={busy} onClick={() => open(selected.id)}>Reload lead</button></div>
      <h3>{selected.name}</h3>
      <dl>{[
        ['Reference', selected.id], ['Created', new Date(selected.createdAt).toLocaleString()], ['Updated', new Date(selected.updatedAt).toLocaleString()],
        ['Country', selected.countryOfResidence], ['Phone', selected.phone], ['Email', selected.email],
        ['Ritual need', selected.ritualIntent], ['Prospect context', selected.broadNeed], ['Preferred sacred location', selected.preferredSacredLocation], ['Wants location guidance', selected.wantsLocationGuidance], ['Knows tithi', selected.knowsTithi],
        ['Timeframe', selected.expectedTimeframe], ['Price consideration', selected.priceConsideration],
        ['Experiment', selected.experimentId], ['Source', selected.source], ['Medium', selected.medium], ['Campaign', selected.campaignName],
        ['Content', selected.content], ['Term', selected.term], ['Landing page', selected.landingPage], ['Referrer', selected.referralUrl]
      ].map(([title, value]) => <React.Fragment key={title}><dt>{title}</dt><dd>{value || 'Not provided'}</dd></React.Fragment>)}</dl>
      <form onSubmit={save}><fieldset disabled={busy}>
        {select('qualificationStatus', qualificationStatuses, 'Qualification status')}
        {select('consultationStatus', consultationStatuses, 'Consultation status')}
        {select('offerStatus', offerStatuses, 'Offer status')}
        {select('primaryObjection', objections, 'Primary objection')}
        <label>Operator notes<textarea rows={5} maxLength={4000} value={selected.operatorNotes || ''} onChange={e => setSelected({ ...selected, operatorNotes: e.target.value })} /></label>
        <p>Keep notes focused on this consultation. Do not enter ancestor details or unnecessary sensitive information.</p>
        <button type="submit">{busy ? 'Saving…' : 'Save changes'}</button>
      </fieldset></form>
    </> : <>
      <button disabled={busy} onClick={() => setRefresh(value => value + 1)}>Refresh leads</button>
      {busy && <p role="status">Loading leads…</p>}
      {!busy && data?.content.length === 0 && <p>No consultation requests yet.</p>}
      <div className="table-scroll"><table><caption>Newest requests first</caption><thead><tr>{['Name / country', 'Contact', 'Created / source', 'Price consideration', 'Qualification', 'Consultation', 'Objection', 'Notes', 'Action'].map(title => <th scope="col" key={title}>{title}</th>)}</tr></thead>
        <tbody>{data?.content.map(lead => <tr key={lead.id}>
          <td>{lead.name}<br />{lead.countryOfResidence}</td><td>{lead.phone}<br />{lead.email}</td>
          <td>{new Date(lead.createdAt).toLocaleDateString()}<br />{lead.source || 'Not provided'}</td>
          <td>{label(lead.priceConsideration)}</td><td>{label(lead.qualificationStatus)}</td><td>{label(lead.consultationStatus)}</td>
          <td>{lead.primaryObjection ? label(lead.primaryObjection) : 'Not recorded'}</td><td>{lead.operatorNotes?.slice(0, 100) || '—'}</td>
          <td><button disabled={busy} onClick={() => open(lead.id)}>Open {lead.name}</button></td>
        </tr>)}</tbody></table></div>
      <div className="pp-v2-actions"><button disabled={busy || page === 0} onClick={() => setPage(value => value - 1)}>Previous</button><span>Page {page + 1}</span><button disabled={busy || !data || page + 1 >= data.totalPages} onClick={() => setPage(value => value + 1)}>Next</button></div>
    </>}
  </section>;
}
